'use client'

import Link from 'next/link'
import { ChevronDown } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PERSONA_SLUGS, personaHref, type PersonaSlug } from '@/lib/personas'

/**
 * The persona menu, in the header.
 *
 * Replaces the switcher section that used to sit above the footer. As a
 * section it was a four-card grid that wrapped to 3 + 1 and repeated on every
 * page; as a menu it is available from anywhere without spending a screen on
 * itself, which is what navigation is supposed to do.
 *
 * A client component only because the dropdown needs state. The list itself is
 * static and the labels arrive resolved, so nothing else crosses the boundary.
 *
 * From `sm` up only. Below that the header has no room for it (the Spanish
 * buttons alone fill a 375px phone), so `PersonaTabs` carries the same links
 * as a row under the header instead.
 */
const PersonaNav = ({
  label,
  personas,
  current,
}: {
  label: string
  personas: Record<PersonaSlug, { name: string, tagline: string }>
  /** Marked rather than hidden: a menu that drops the page you are on makes
      the reader work out what is missing. */
  current?: PersonaSlug
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className='group flex items-center gap-1 rounded-full px-3 py-2 font-gilroy text-sm font-semibold text-muted-foreground outline-none transition-colors hover:text-foreground data-[state=open]:text-foreground'>
        {label}
        <ChevronDown className='size-4 transition-transform duration-200 group-data-[state=open]:rotate-180' aria-hidden='true' />
      </DropdownMenuTrigger>

      <DropdownMenuContent align='start' className='w-64 rounded-2xl p-2'>
        {PERSONA_SLUGS.map(slug => (
          <DropdownMenuItem key={slug} asChild className='rounded-xl p-0'>
            <Link
              href={personaHref(slug)}
              aria-current={slug === current ? 'page' : undefined}
              className={`flex cursor-pointer flex-col items-start gap-0.5 px-3 py-2.5 ${
                slug === current ? 'bg-muted' : ''
              }`}
            >
              <span className='font-gilroy text-sm font-bold'>{personas[slug].name}</span>
              <span className='text-xs font-medium text-muted-foreground'>{personas[slug].tagline}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default PersonaNav
